/* Circle D — in-chat order status, with Front behind it.
 *
 * The launcher opens a panel with three views: a menu, an order lookup, and the
 * result. "Check order status" resolves the order HERE — build stage, production
 * date and tracking — instead of collecting three fields and opening a human
 * conversation, which is what the Front bot's version of this did and what the
 * whole exercise is meant to stop.
 *
 * Things worth knowing before changing this:
 *
 *  1. The lookup calls /apps/service/order-status?compact=1, a Shopify App
 *     Proxy path. Shopify HMAC-signs the request on the way through, and the
 *     app still requires the order number AND the email on the order to match,
 *     so this endpoint is no more permissive than the page it mirrors. Never
 *     point it at anything but a same-origin /apps/ path — a cross-origin fetch
 *     loses the proxy signature and would need its own auth.
 *
 *  2. The result JSON carries `fullUrl` with a short-lived signed token, so
 *     "View full details" opens the real page with nothing retyped and no email
 *     address in the URL. Use it verbatim; do not rebuild that link here.
 *
 *  3. Front is initialised with useDefaultLauncher:false and its bundle is NOT
 *     fetched until "Talk to a person" is clicked. FrontChat does not exist for
 *     most visitors — never assume it is there.
 *
 *  4. The status menu item is a real anchor to /pages/order-status. We
 *     preventDefault it; with JS broken it still navigates. Keep it that way.
 *
 *  5. Front can accept the hand-off and still show nothing. Two ways, both seen
 *     in production: outside its configured online hours it hides the widget and
 *     'show' is a silent no-op, and it rehydrates a chatbot flow position saved
 *     in localStorage, which replays an old prompt list the interpreter has
 *     already moved past. Both are handled below — never treat a returned
 *     'show' call as proof the visitor is looking at a chat window.
 */
(function () {
  'use strict';

  var CHAT_ID = document.currentScript && document.currentScript.getAttribute('data-chat-id');
  var SEEN_KEY = 'cds-chat-started';
  var FRONT_STORE_KEY = 'frontChat:store';

  var root = document.getElementById('cds-chat');
  if (!root || !CHAT_ID) return;

  var launcher = root.querySelector('[data-chat-launcher]');
  var panel = root.querySelector('[data-chat-panel]');
  var startBtn = root.querySelector('[data-chat-start]');
  var offlineBox = root.querySelector('[data-chat-offline]');
  var offlineTitle = root.querySelector('[data-chat-offline-title]');
  var offlineSub = root.querySelector('[data-chat-offline-sub]');
  var retryBtn = root.querySelector('[data-chat-retry]');
  var statusLink = root.querySelector('[data-chat-status]');
  var form = root.querySelector('[data-chat-form]');
  var errBox = root.querySelector('[data-chat-error]');
  var resultBox = root.querySelector('[data-chat-result]');
  if (!launcher || !panel || !startBtn) return;

  var views = {};
  Array.prototype.forEach.call(root.querySelectorAll('[data-chat-view]'), function (v) {
    views[v.getAttribute('data-chat-view')] = v;
  });

  var frontState = 'idle'; // idle | loading | ready | failed
  var pendingOpen = false; // a click that arrived while the bundle was loading

  function started() {
    try { return sessionStorage.getItem(SEEN_KEY) === '1'; } catch (e) { return false; }
  }
  function markStarted() {
    try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* private mode */ }
  }

  /* ---------- Front state that outlives the visit ---------- */

  // Front keeps the chatbot's position in its decision tree in localStorage,
  // under frontChat:store → chatbotInterpreter, and it survives closing the
  // widget, the tab and the browser. A visitor who came back days later got the
  // previous conversation's prompt list drawn again while the interpreter still
  // sat on a node from that old visit, so clicking an option — "Talk to a
  // person" included — did nothing at all: the option no longer belonged to the
  // current node. Clearing it hands Front a clean flow, which starts at the
  // greeting.
  //
  // frontChat:status is deliberately left alone. It holds the visitor and
  // conversation ids, so a reply from a real person still finds them and their
  // history is not thrown away — only the bot's place in the script is.
  function resetChatbotFlow() {
    var raw;
    try { raw = localStorage.getItem(FRONT_STORE_KEY); } catch (e) { return; }
    if (!raw) return;
    try {
      var store = JSON.parse(raw);
      delete store.chatbotInterpreter;
      delete store.engageInterpreter;
      localStorage.setItem(FRONT_STORE_KEY, JSON.stringify(store));
    } catch (e) {
      // Unparseable is worse than absent — the whole point is a clean start.
      try { localStorage.removeItem(FRONT_STORE_KEY); } catch (e2) { /* private mode */ }
    }
  }

  /* ---------- business hours ---------- */

  // Mon–Fri 8–5 Central, the same window the contact page paints its badges
  // from. This is a label and a shortcut, never the last word: Front's own
  // schedule is authoritative, so an open verdict is still verified after the
  // hand-off, and a closed one still offers a way through.
  function shopState() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago', hour12: false, weekday: 'short', hour: 'numeric'
      }).formatToParts(new Date());
      var get = function (t) {
        return (parts.filter(function (p) { return p.type === t; })[0] || {}).value;
      };
      var day = get('weekday'), hour = parseInt(get('hour'), 10);
      var weekend = ['Sat', 'Sun'].indexOf(day) !== -1;
      if (!weekend && hour >= 8 && hour < 17) return { open: true, back: '' };
      if (day === 'Sat' || day === 'Sun' || (day === 'Fri' && hour >= 17)) {
        return { open: false, back: 'Chat is back Monday at 8am CT.' };
      }
      if (!weekend && hour < 8) return { open: false, back: 'Chat is back at 8am CT.' };
      return { open: false, back: 'Chat is back tomorrow at 8am CT.' };
    } catch (e) {
      return { open: false, back: 'Chat runs Mon\u2013Fri, 8am\u20135pm CT.' };
    }
  }

  // Front hides the widget outside its online hours and 'show' then does
  // nothing and reports nothing. The only honest signal that a visitor is
  // actually looking at a chat window is the iframe having real size.
  function chatUp() {
    if (typeof window.FrontChat !== 'function') return false;
    var f = document.getElementById('front-chat-iframe');
    if (!f) return false;
    var r = f.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function show(name) {
    Object.keys(views).forEach(function (k) { views[k].hidden = k !== name; });
    panel.scrollTop = 0;
  }

  /* ---------- panel ---------- */

  function openPanel() {
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    // Focus the first control only when the launcher was reached by keyboard —
    // moving focus after a mouse click makes the panel feel like it stole the
    // page.
    if (root.classList.contains('kb')) {
      var first = panel.querySelector('a, button, input');
      if (first) first.focus();
    }
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('pointerdown', onOutside, true);
  }

  function closePanel(refocus) {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onOutside, true);
    if (refocus) launcher.focus();
  }

  function onKeydown(e) {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    // Escape backs out one level before closing: someone reading a result
    // should not lose the panel entirely on the first press.
    if (!views.menu.hidden) closePanel(true); else show('menu');
  }
  function onOutside(e) { if (!root.contains(e.target)) closePanel(false); }

  launcher.addEventListener('mousedown', function () { root.classList.remove('kb'); });
  launcher.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') root.classList.add('kb');
  });

  launcher.addEventListener('click', function () {
    if (started()) { openChat(); return; }          // mid-conversation: skip the menu
    if (panel.hidden) { show('menu'); openPanel(); } else closePanel(true);
  });

  Array.prototype.forEach.call(root.querySelectorAll('[data-chat-back]'), function (b) {
    b.addEventListener('click', function () { show('menu'); launcher.focus(); });
  });

  /* ---------- order status ---------- */

  if (statusLink && form) {
    statusLink.addEventListener('click', function (e) {
      e.preventDefault();                            // anchor stays the no-JS path
      show('form');
      var f = form.querySelector('input');
      if (f) f.focus();
    });
  }

  function setError(msg) {
    if (!errBox) return;
    errBox.textContent = msg || '';
    errBox.hidden = !msg;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** Draws the card. Everything rendered here comes from the compact endpoint,
   *  which derives it from the SAME step list the full page uses — so the card
   *  and the page a customer opens next cannot disagree. */
  function renderResult(d) {
    var steps = d.steps || [];
    var idx = typeof d.currentIndex === 'number' ? d.currentIndex : -1;

    var bar = idx < 0 ? '' :
      '<div class="cds-chat__bar">' + steps.map(function (_, i) {
        return '<i class="' + (i <= idx ? 'on' : '') + '"></i>';
      }).join('') + '</div>' +
      '<div class="cds-chat__steps">' + steps.map(function (s, i) {
        return '<span class="' + (i === idx ? 'cur' : i < idx ? 'done' : '') + '">' + esc(s) + '</span>';
      }).join('') + '</div>';

    var date = d.productionDate
      ? '<div class="cds-chat__date"><div class="k">Estimated Production Date</div>' +
        '<div class="v">' + esc(d.productionDate) + '</div></div>'
      : '';

    var ships = (d.shipments || []).map(function (s) {
      var links = (s.trackingUrls || []).length
        ? (s.tracking || []).map(function (t, i) {
            var u = s.trackingUrls[i];
            return u ? '<a href="' + esc(u) + '" target="_blank" rel="noopener">' + esc(t) + '</a>' : esc(t);
          }).join(', ')
        : (s.tracking || []).map(esc).join(', ');
      return '<div class="cds-chat__ship"><b>' + esc(s.carrier) + '</b>' +
        (s.date ? ' &middot; ' + esc(s.date) : '') +
        (links ? '<br>' + links : '') + '</div>';
    }).join('');

    resultBox.innerHTML =
      '<div class="cds-chat__ordno">Order ' + esc(d.orderName) + '</div>' +
      '<div class="cds-chat__head">' + esc(d.headline) + '</div>' +
      // In Production ships an empty blurb on purpose (see STEPS in
      // order-status-html.ts). Emitting the element anyway leaves its margin
      // under the headline, so skip it entirely when there is nothing to say.
      (d.blurb ? '<div class="cds-chat__blurb">' + esc(d.blurb) + '</div>' : '') +
      bar + date + ships +
      '<a class="cds-chat__full" href="' + esc(d.fullUrl) + '">View Full Details</a>' +
      '<button type="button" class="cds-chat__again" data-chat-again>Check Another Order</button>';

    var again = resultBox.querySelector('[data-chat-again]');
    if (again) {
      again.addEventListener('click', function () {
        setError('');
        show('form');
        var f = form && form.querySelector('input');
        if (f) f.focus();
      });
    }
    show('result');
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var order = (form.order && form.order.value || '').trim();
      var email = (form.email && form.email.value || '').trim();
      if (!order || !email) { setError('Enter your order number and the email on the order.'); return; }

      var submit = form.querySelector('[data-chat-submit]');
      setError('');
      if (submit) { submit.setAttribute('aria-busy', 'true'); submit.disabled = true; }

      var qs = 'order=' + encodeURIComponent(order) + '&email=' + encodeURIComponent(email) + '&compact=1';
      fetch('/apps/service/order-status?' + qs, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (d) {
          if (d && d.ok) { renderResult(d); return; }
          // A structured error from the app is a real answer (wrong number,
          // wrong email); anything else means we never got one.
          setError((d && d.error) || 'We could not check that order just now. Please try again.');
        })
        .catch(function () {
          setError('We could not reach our order system. Check your connection and try again.');
        })
        .then(function () {
          if (submit) { submit.removeAttribute('aria-busy'); submit.disabled = false; }
        });
    });
  }

  /* ---------- Front, loaded on demand ---------- */

  // ⚠️ FrontChat('init') is ASYNCHRONOUS. Calling 'show' on the next line
  // creates the iframe at 0x0 and never opens it — the bundle loads, the button
  // stops spinning, and nothing appears. onInitCompleted is the only signal
  // that the widget is ready, so every 'show' is queued behind it. Do not
  // "simplify" this back into a straight-line init/show pair.
  function initFront(done) {
    if (!window.FrontChat) { done(false); return; }
    var settled = false;
    var finish = function (ok) { if (!settled) { settled = true; done(ok); } };
    try {
      window.FrontChat('init', {
        chatId: CHAT_ID,
        useDefaultLauncher: false,
        onInitCompleted: function () { finish(true); },
      });
    } catch (e) { finish(false); return; }
    // Belt and braces: if Front ever stops firing the callback, do not leave the
    // visitor with a button that has already stopped looking busy.
    setTimeout(function () { finish(!!window.FrontChat); }, 6000);
  }

  function showFront() {
    try { window.FrontChat('show'); } catch (e) { /* nothing useful to do */ }
  }

  /* ---------- when chat cannot answer ---------- */

  // One screen for every dead end — after hours, a bundle that never arrived, a
  // 'show' that did nothing — because they are the same problem for the person
  // reading it: nobody is going to type back right now, so give them a route
  // that does get answered.
  function showOffline(opts) {
    opts = opts || {};
    startBtn.hidden = true;
    startBtn.removeAttribute('aria-busy');
    if (offlineTitle) {
      offlineTitle.textContent = opts.title || 'Live chat is offline';
    }
    if (offlineSub) {
      var back = shopState().back;
      offlineSub.textContent = opts.sub || (
        // Inside our hours and still nothing: Front's own schedule differs, or
        // it declined to open. Say that, rather than quoting hours the visitor
        // can see are current.
        back ? back + ' Leave a message and it gets read first thing.'
             : 'We could not open the chat window just now. Leave a message and it gets read first thing.'
      );
    }
    // Only worth offering when we closed the door on our own clock. If Front
    // itself refused to open, clicking again just fails again.
    if (retryBtn) retryBtn.hidden = !opts.retry;
    if (offlineBox) offlineBox.hidden = false;
    if (panel.hidden) { show('menu'); openPanel(); }
    // The panel scrolls, and this block replaces something at the very bottom of
    // it — reveal it, or on a short screen the visitor sees the menu unchanged
    // and reads it as another click that did nothing.
    // scrollTop, not scrollIntoView: the panel hangs off a fixed launcher, and
    // scrollIntoView would walk up and scroll the page under it.
    panel.scrollTop = panel.scrollHeight;
  }

  function failOpen() {
    frontState = 'failed';
    showOffline({
      title: 'Chat is not loading',
      sub: 'Something is blocking our chat from starting. Send a message instead and we will pick it up.'
    });
  }

  // Front's 'show' resolves nothing and throws nothing when the widget is
  // hidden, so the hand-off is only real once the iframe has size. Poll briefly
  // rather than trusting the call, and put the visitor back in our own panel if
  // it never appears.
  function confirmOpened() {
    var tries = 0;
    (function poll() {
      if (chatUp()) { markStarted(); closePanel(false); return; }
      if (++tries > 6) {                     // ~2.4s, then stop pretending
        frontState = 'failed';
        showOffline();
        return;
      }
      setTimeout(poll, 400);
    })();
  }

  function handOff() {
    showFront();
    confirmOpened();
  }

  function openChat(force) {
    // Hours are checked before anything to do with Front's state, because this
    // is the one answer that is always available. Someone mid-conversation
    // skips our menu entirely, so a click swallowed while the bundle loads
    // leaves them looking at a page where nothing happened — the exact
    // complaint this whole change is here to fix. Outside our own hours, do not
    // load a bundle whose widget is configured not to appear; say so, and leave
    // a way through in case Front's schedule and ours ever disagree.
    if (!force && !shopState().open) {
      showOffline({ retry: true });
      return;
    }
    if (frontState === 'failed') { showOffline(); return; }
    if (frontState === 'ready') { handOff(); return; }
    // Warming in the background: hand off the moment it lands, rather than
    // dropping the click.
    if (frontState === 'loading') { pendingOpen = true; return; }

    frontState = 'loading';
    startBtn.setAttribute('aria-busy', 'true');
    resetChatbotFlow();                      // must precede the bundle: it rehydrates on load

    var s = document.createElement('script');
    s.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    // A stalled network is the case that matters: onerror never fires, so the
    // button would spin forever without this.
    var timer = setTimeout(function () { if (frontState === 'loading') failOpen(); }, 8000);
    s.onload = function () {
      clearTimeout(timer);
      initFront(function (ok) {
        if (!ok) { failOpen(); return; }
        frontState = 'ready';
        pendingOpen = false;
        startBtn.removeAttribute('aria-busy');
        handOff();
      });
    };
    s.onerror = function () { clearTimeout(timer); failOpen(); };
    document.head.appendChild(s);
  }

  if (retryBtn) {
    retryBtn.addEventListener('click', function () {
      if (offlineBox) offlineBox.hidden = true;
      startBtn.hidden = false;
      openChat(true);
    });
  }

  // Paint the button before anyone clicks it. "Talk to a Person" at 9pm is a
  // promise the widget will not keep.
  if (!shopState().open) startBtn.textContent = 'Leave a Message';

  startBtn.addEventListener('click', function () { openChat(false); });

  // Someone already mid-conversation gets Front warmed on idle, so their next
  // click opens instantly instead of spinning.
  if (started()) {
    (window.requestIdleCallback || function (f) { setTimeout(f, 3000); })(function () {
      if (frontState !== 'idle') return;
      frontState = 'loading';
      // No reset here: this visitor is mid-conversation in this session, and
      // their place in the flow is the one thing worth keeping.
      var s = document.createElement('script');
      s.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
      s.onload = function () {
        initFront(function (ok) {
          frontState = ok ? 'ready' : 'idle';
          if (!pendingOpen) return;
          pendingOpen = false;
          if (ok) { handOff(); } else { failOpen(); }
        });
      };
      s.onerror = function () {
        frontState = 'idle';
        if (pendingOpen) { pendingOpen = false; failOpen(); }
      };
      document.head.appendChild(s);
    });
  }
})();
